import { computed, ref, watch } from 'vue';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcClient } from '~/services';
import type { TcrBrand } from '~/services/adminTenDlcClient.types';
import debounce from '~/utils/debounce';

type BrandOption = { label: string; value: string };

const toOption = (brand: TcrBrand): BrandOption => ({
    value: brand.brand_id,
    label: null === brand.display_name ? brand.brand_id : `${brand.brand_id} - ${brand.display_name}`,
});

export const useTcrBrandOptions = (selectedBrandId: () => string | undefined) => {
    const toasted = useToasted();
    const searchResults = ref<BrandOption[]>([]);
    const selectedOption = ref<BrandOption>();
    const loadingBrands = ref(false);

    // vue-select labels the selection from the options it is given, and a search only
    // returns the first page, so the selected brand is kept in the options on its own.
    const brandOptions = computed(() => {
        const selected = selectedOption.value;

        if (undefined === selected || searchResults.value.some((option) => option.value === selected.value)) {
            return searchResults.value;
        }

        return [selected, ...searchResults.value];
    });

    const searchBrands = debounce((query?: string) => {
        loadingBrands.value = true;

        adminTenDlcClient
            .getTcrBrands({ query })
            .then((response) => {
                searchResults.value = response.data.data.map(toOption);
            })
            .catch((e) => {
                console.error(e);
                toasted.global.platform_error();
            })
            .finally(() => {
                loadingBrands.value = false;
            });
    }, 300);

    watch(
        selectedBrandId,
        (brandId) => {
            if (undefined === brandId) {
                selectedOption.value = undefined;

                return;
            }

            const known = brandOptions.value.find((option) => option.value === brandId);

            if (undefined !== known) {
                selectedOption.value = known;

                return;
            }

            adminTenDlcClient
                .getTcrBrands({ brand_ids: [brandId] })
                .then((response) => {
                    const brand = response.data.data.find((candidate) => candidate.brand_id === brandId);

                    selectedOption.value = undefined === brand ? undefined : toOption(brand);
                })
                .catch((e) => {
                    console.error(e);
                    toasted.global.platform_error();
                });
        },
        { immediate: true }
    );

    return { brandOptions, loadingBrands, searchBrands };
};
